import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { fuelLogAPI, vehicleAPI, driverAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
    Plus, Search, Fuel, Edit, Trash2, Loader2, 
    MoreVertical, Filter, Calendar, Gauge 
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const initialFormData = {
    vehicle_id: '',
    driver_id: '',
    date: new Date().toISOString().split('T')[0],
    quantity_liters: '',
    price_per_liter: '',
    total_cost: '',
    odometer_reading: '',
    fuel_station: '',
    fuel_type: 'diesel',
    notes: ''
};

const fuelTypes = ['diesel', 'gasoline', 'electric', 'cng', 'lpg'];

export default function FuelLogsPage() {
    const { canManage, isAdmin } = useAuth();
    const [logs, setLogs] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterVehicle, setFilterVehicle] = useState('all');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingLog, setEditingLog] = useState(null);
    const [formData, setFormData] = useState(initialFormData);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [logsRes, vehiclesRes, driversRes] = await Promise.all([
                fuelLogAPI.getAll(),
                vehicleAPI.getAll(),
                driverAPI.getAll().catch(() => ({ data: [] }))
            ]);
            setLogs(logsRes.data);
            setVehicles(vehiclesRes.data);
            setDrivers(driversRes.data);
        } catch (error) {
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const calculateTotalCost = (quantity, price) => {
        const qty = parseFloat(quantity) || 0;
        const prc = parseFloat(price) || 0;
        return (qty * prc).toFixed(2);
    };

    const handleQuantityChange = (e) => {
        const quantity = e.target.value;
        setFormData({
            ...formData, 
            quantity_liters: quantity,
            total_cost: calculateTotalCost(quantity, formData.price_per_liter)
        });
    };

    const handlePriceChange = (e) => {
        const price = e.target.value;
        setFormData({
            ...formData, 
            price_per_liter: price,
            total_cost: calculateTotalCost(formData.quantity_liters, price)
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const payload = {
                vehicle_id: formData.vehicle_id,
                driver_id: formData.driver_id === 'none' ? null : formData.driver_id || null,
                date: formData.date,
                quantity_liters: parseFloat(formData.quantity_liters),
                price_per_liter: parseFloat(formData.price_per_liter),
                total_cost: parseFloat(formData.total_cost),
                odometer_reading: parseFloat(formData.odometer_reading),
                fuel_station: formData.fuel_station || null,
                fuel_type: formData.fuel_type,
                notes: formData.notes || null
            };

            if (editingLog) {
                await fuelLogAPI.update(editingLog.id, payload);
                toast.success('Fuel log updated');
            } else {
                await fuelLogAPI.create(payload);
                toast.success('Fuel log created');
            }
            
            setIsDialogOpen(false);
            setEditingLog(null);
            setFormData(initialFormData);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Operation failed');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (log) => {
        setEditingLog(log);
        setFormData({
            vehicle_id: log.vehicle_id,
            driver_id: log.driver_id || '',
            date: log.date,
            quantity_liters: log.quantity_liters,
            price_per_liter: log.price_per_liter,
            total_cost: log.total_cost,
            odometer_reading: log.odometer_reading,
            fuel_station: log.fuel_station || '',
            fuel_type: log.fuel_type,
            notes: log.notes || ''
        });
        setIsDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this log?')) return;
        
        try {
            await fuelLogAPI.delete(id);
            toast.success('Fuel log deleted');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Delete failed');
        }
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setEditingLog(null);
        setFormData(initialFormData);
    };

    const getVehicleInfo = (vehicleId) => {
        const vehicle = vehicles.find(v => v.id === vehicleId);
        return vehicle ? `${vehicle.make} ${vehicle.model} (${vehicle.license_plate})` : 'Unknown';
    };

    const getDriverName = (driverId) => {
        const driver = drivers.find(d => d.id === driverId);
        return driver?.name || '-';
    };

    const filteredLogs = logs.filter(log => {
        const vehicle = vehicles.find(v => v.id === log.vehicle_id);
        const matchesSearch = 
            (vehicle && vehicle.license_plate.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (log.fuel_station && log.fuel_station.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesVehicle = filterVehicle === 'all' || log.vehicle_id === filterVehicle;
        return matchesSearch && matchesVehicle;
    });

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="fuel-logs-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Fuel Logs</h1>
                    <p className="text-zinc-500">{logs.length} total entries</p>
                </div>
                
                <Dialog open={isDialogOpen} onOpenChange={(open) => open ? setIsDialogOpen(true) : handleDialogClose()}>
                    <DialogTrigger asChild>
                        <Button 
                            className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm"
                            data-testid="add-fuel-log-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Fuel Log
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="text-slate-800">
                                {editingLog ? 'Edit Fuel Log' : 'Add New Fuel Log'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <Label className="form-label">Vehicle</Label>
                                    <Select 
                                        value={formData.vehicle_id} 
                                        onValueChange={(value) => setFormData({...formData, vehicle_id: value})}
                                    >
                                        <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="fuel-vehicle-select">
                                            <SelectValue placeholder="Select vehicle" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white border-slate-200">
                                            {vehicles.map(vehicle => (
                                                <SelectItem key={vehicle.id} value={vehicle.id}>
                                                    {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="form-group">
                                    <Label className="form-label">Driver (Optional)</Label>
                                    <Select 
                                        value={formData.driver_id} 
                                        onValueChange={(value) => setFormData({...formData, driver_id: value})}
                                    >
                                        <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="fuel-driver-select">
                                            <SelectValue placeholder="Select driver" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white border-slate-200">
                                            <SelectItem value="none">No driver</SelectItem>
                                            {drivers.map(driver => (
                                                <SelectItem key={driver.id} value={driver.id}>
                                                    {driver.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <Label className="form-label">Date</Label>
                                    <Input
                                        type="date"
                                        value={formData.date}
                                        onChange={(e) => setFormData({...formData, date: e.target.value})}
                                        className="bg-white border-slate-300 text-slate-900"
                                        required
                                        data-testid="fuel-date-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <Label className="form-label">Fuel Type</Label>
                                    <Select 
                                        value={formData.fuel_type} 
                                        onValueChange={(value) => setFormData({...formData, fuel_type: value})}
                                    >
                                        <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="fuel-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white border-slate-200">
                                            {fuelTypes.map(type => (
                                                <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div className="form-group">
                                    <Label className="form-label">Quantity (Liters)</Label>
                                    <Input
                                        type="number"
                                        value={formData.quantity_liters}
                                        onChange={handleQuantityChange}
                                        className="bg-white border-slate-300 text-slate-900"
                                        min="0"
                                        step="0.01"
                                        required
                                        data-testid="fuel-quantity-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <Label className="form-label">Price/Liter ($)</Label>
                                    <Input
                                        type="number"
                                        value={formData.price_per_liter}
                                        onChange={handlePriceChange}
                                        className="bg-white border-slate-300 text-slate-900"
                                        min="0"
                                        step="0.01"
                                        required
                                        data-testid="fuel-price-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <Label className="form-label">Total Cost ($)</Label>
                                    <Input
                                        type="number"
                                        value={formData.total_cost}
                                        onChange={(e) => setFormData({...formData, total_cost: e.target.value})}
                                        className="bg-white border-slate-300 text-slate-900"
                                        min="0"
                                        step="0.01"
                                        required
                                        data-testid="fuel-total-input"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <Label className="form-label">Odometer Reading (km)</Label>
                                    <Input
                                        type="number"
                                        value={formData.odometer_reading}
                                        onChange={(e) => setFormData({...formData, odometer_reading: e.target.value})}
                                        className="bg-white border-slate-300 text-slate-900"
                                        min="0"
                                        required
                                        data-testid="fuel-odometer-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <Label className="form-label">Fuel Station</Label>
                                    <Input
                                        value={formData.fuel_station}
                                        onChange={(e) => setFormData({...formData, fuel_station: e.target.value})}
                                        className="bg-white border-slate-300 text-slate-900"
                                        placeholder="Station name"
                                        data-testid="fuel-station-input"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <Label className="form-label">Notes</Label>
                                <Textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                    className="bg-white border-slate-300 text-slate-900"
                                    placeholder="Additional notes..."
                                    data-testid="fuel-notes-input"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    onClick={handleDialogClose}
                                    className="border-zinc-700 text-slate-500 hover:text-slate-700"
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    type="submit" 
                                    className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white"
                                    disabled={submitting}
                                    data-testid="fuel-submit-btn"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingLog ? 'Update' : 'Create')}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative sm:w-1/2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                        placeholder="Search by vehicle or station..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-white border-slate-300 text-slate-900"
                        data-testid="fuel-search-input"
                    />
                </div>
                <Select value={filterVehicle} onValueChange={setFilterVehicle}>
                    <SelectTrigger className="sm:w-1/2 bg-white border-slate-300 text-slate-900" data-testid="fuel-filter-vehicle">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="all">All Vehicles</SelectItem>
                        {vehicles.map(vehicle => (
                            <SelectItem key={vehicle.id} value={vehicle.id}>
                                {vehicle.license_plate}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <div className="fleet-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Vehicle</th>
                                <th>Driver</th>
                                <th>Quantity</th>
                                <th>Price/L</th>
                                <th>Total</th>
                                <th>Odometer</th>
                                {canManage() && <th className="w-12">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLogs.length === 0 ? (
                                <tr>
                                    <td colSpan={canManage() ? 8 : 7} className="text-center py-8">
                                        <div className="empty-state">
                                            <Fuel className="empty-state-icon mx-auto" />
                                            <p>No fuel logs found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredLogs.map((log) => (
                                    <tr key={log.id} className="table-row-hover" data-testid={`fuel-row-${log.id}`}>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-zinc-500" />
                                                <span>{new Date(log.date).toLocaleDateString()}</span>
                                            </div>
                                        </td>
                                        <td className="text-white">{getVehicleInfo(log.vehicle_id)}</td>
                                        <td>{getDriverName(log.driver_id)}</td>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <Fuel className="w-4 h-4 text-green-500" />
                                                <span>{log.quantity_liters.toFixed(2)} L</span>
                                            </div>
                                        </td>
                                        <td>{formatCurrency(log.price_per_liter)}</td>
                                        <td className="text-[#1B7EB5] font-medium">{formatCurrency(log.total_cost)}</td>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <Gauge className="w-4 h-4 text-zinc-500" />
                                                <span>{log.odometer_reading.toLocaleString()} km</span>
                                            </div>
                                        </td>
                                        {canManage() && (
                                            <td>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-700">
                                                            <MoreVertical className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="bg-white border-slate-200">
                                                        <DropdownMenuItem 
                                                            onClick={() => handleEdit(log)}
                                                            className="cursor-pointer"
                                                        >
                                                            <Edit className="w-4 h-4 mr-2" />
                                                            Edit
                                                        </DropdownMenuItem>
                                                        {isAdmin() && (
                                                            <DropdownMenuItem 
                                                                onClick={() => handleDelete(log.id)}
                                                                className="cursor-pointer text-red-400 focus:text-red-400"
                                                            >
                                                                <Trash2 className="w-4 h-4 mr-2" />
                                                                Delete
                                                            </DropdownMenuItem>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
